import { Card, Tabs } from '@payaca/ambrosia';
import { useQueryClient } from '@tanstack/react-query';
import { format, startOfMinute } from 'date-fns';
import { FC, useCallback, useMemo } from 'react';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import noteKeys from '@/api/queries/notes/keyFactory';
import projectKeys from '@/api/queries/project/keyFactory';
import useGetProjectEvents from '@/api/queries/project/useGetProjectEvents';
import { getProjectOverviewActivityFilterPreferencesLocalStorageKey } from '@/helpers/localStorageKeyHelper';
import CreateEditNoteModal from '@/ui/components/createEditNoteModal/CreateEditNoteModal';
import DealActivityCard from '@/ui/components/dealActivityCard/DealActivityCard';
import ActivityList from '@/ui/pages/projectPage/components/ActivityPanel/ActivityList';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';

const ActivityPanel: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const queryClient = useQueryClient();
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const isAddingOrEditingNote = useRouteMatch<{
    noteId: string;
  }>({
    path: `${path}/:noteId`,
    exact: true,
  });
  const { events: upcomingProjectEvents } = useGetProjectEvents(projectId, {
    start: startOfMinute(new Date()).toISOString(),
  });

  const defaultTabIndex = useMemo(() => {
    const allFilterTabPreferences = JSON.parse(
      localStorage.getItem(
        getProjectOverviewActivityFilterPreferencesLocalStorageKey()
      ) ?? '{}'
    );

    return allFilterTabPreferences[projectId] ?? 0;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'projectId'
  }, []);

  const handleFilterTabChange = useCallback((index: number) => {
    const _filterTabPreferences = JSON.parse(
      localStorage.getItem(
        getProjectOverviewActivityFilterPreferencesLocalStorageKey()
      ) ?? '{}'
    );

    localStorage.setItem(
      getProjectOverviewActivityFilterPreferencesLocalStorageKey(),
      JSON.stringify({
        ..._filterTabPreferences,
        [projectId]: index,
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'projectId'
  }, []);

  return (
    <>
      <Card>
        <Card.Body>
          <Tabs
            variant="segment"
            defaultIndex={defaultTabIndex}
            onChange={handleFilterTabChange}
          >
            <Tabs.Panel
              label="All"
              render={() => (
                <div className="space-y-4">
                  {upcomingProjectEvents &&
                    upcomingProjectEvents.items.length > 0 && (
                      <>
                        <h4>Upcoming</h4>

                        {upcomingProjectEvents.items.map((event) => (
                          <DealActivityCard
                            key={event.id}
                            date={new Date(event.beginAt)}
                            onClick={() =>
                              history.push(
                                `/projects/${projectId}/schedule/${event.id}`
                              )
                            }
                            title={
                              <>
                                <p className="font-semibold">{event.name}</p>
                              </>
                            }
                            titleSuffix={
                              <span className="whitespace-nowrap">
                                {format(
                                  event.beginAt,
                                  getInternationalDateFormatByLocale(
                                    DateFormats.TIMESTAMP_SHORT,
                                    accountRegionPreferences?.locale
                                  )
                                )}
                              </span>
                            }
                          >
                            <p className="supporting-body whitespace-pre-wrap">
                              {event.description}
                            </p>
                          </DealActivityCard>
                        ))}
                      </>
                    )}

                  <ActivityList activityFilter={[]} />
                </div>
              )}
            />
            <Tabs.Panel
              label="Notes"
              render={() => <ActivityList activityFilter={['note']} />}
            />
            <Tabs.Panel
              label="Communications"
              render={() => <ActivityList activityFilter={['communication']} />}
            />
          </Tabs>
        </Card.Body>
      </Card>

      <CreateEditNoteModal
        projectId={projectId}
        isOpen={!!isAddingOrEditingNote}
        onClose={() => {
          history.push(url);
        }}
        notePublicId={
          isAddingOrEditingNote?.params.noteId !== 'addNote'
            ? isAddingOrEditingNote?.params.noteId
            : undefined
        }
        onSuccess={() => {
          void queryClient.invalidateQueries({
            queryKey: projectKeys.activityFeed(projectId),
          });

          if (isAddingOrEditingNote?.params.noteId) {
            void queryClient.invalidateQueries({
              queryKey: noteKeys.note(isAddingOrEditingNote.params.noteId),
            });
          }
        }}
        onSuccessDelete={() => {
          void queryClient.invalidateQueries({
            queryKey: projectKeys.activityFeed(projectId),
          });

          if (isAddingOrEditingNote?.params.noteId) {
            // oxlint-disable-next-line typescript/no-meaningless-void-operator -- FIXME: pre-existing error: void operator shouldn't be used on void; it should convey that a return value is being ignored
            void queryClient.removeQueries({
              queryKey: noteKeys.note(isAddingOrEditingNote.params.noteId),
            });
          }
        }}
      />
    </>
  );
};

export default ActivityPanel;
